import React, { useState, useRef } from 'react';
import { useMap, TERRAIN_MAP } from '../../contexts/MapContext';
import { useCampaign } from '../../contexts/CampaignContext';
import { OBJECT_MAP } from '../../lib/objects';

const WORKER_URL = import.meta.env.VITE_WORKER_URL || '';

// Status messages shown during the multi-step import
const STEPS = [
  'Sending map to Claude…',
  'Analyzing terrain…',
  'Building grid…',
  'Loading map…',
];

export default function AiMapImport() {
  const { loadFullMap, gridW: GRID_W, gridH: GRID_H, spawnPoint } = useMap();
  const { spawnToken, tokens, moveToken } = useCampaign();

  const [open,    setOpen]    = useState(false);
  const [preview, setPreview] = useState(null);   // base64 data-URL
  const [status,  setStatus]  = useState(null);   // null | 'loading' | 'success' | 'error'
  const [message, setMessage] = useState('');
  const [stepIdx, setStepIdx] = useState(0);

  const fileRef    = useRef(null);
  const stepTimer  = useRef(null);

  // ── File picker ───────────────────────────────────────────────────────────
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setStatus(null);
    setMessage('');
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const clearFile = () => {
    setPreview(null);
    setStatus(null);
    setMessage('');
    if (fileRef.current) fileRef.current.value = '';
  };

  // ── Animate loading steps ─────────────────────────────────────────────────
  const startStepAnimation = () => {
    let i = 0;
    setStepIdx(0);
    stepTimer.current = setInterval(() => {
      i = Math.min(i + 1, STEPS.length - 1);
      setStepIdx(i);
    }, 2500);
  };

  const stopStepAnimation = () => {
    clearInterval(stepTimer.current);
  };

  // ── Import ────────────────────────────────────────────────────────────────
  const handleImport = async () => {
    if (!preview || status === 'loading') return;

    if (!WORKER_URL) {
      setStatus('error');
      setMessage('VITE_WORKER_URL is not set. Deploy the Cloudflare Worker and add the env var.');
      return;
    }

    setStatus('loading');
    setMessage('');
    startStepAnimation();

    try {
      // Strip the data-URL header to isolate raw base64 + media type
      const [header, imageBase64] = preview.split(',');
      const mediaType = header.match(/:(.*?);/)?.[1] ?? 'image/jpeg';

      const res = await fetch(`${WORKER_URL}/api/ai-map-import`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ imageBase64, mediaType, gridW: GRID_W, gridH: GRID_H }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Worker responded ${res.status}`);
      }

      const json = await res.json();
      const terrainStrings = json.cells;

      if (!Array.isArray(terrainStrings)) {
        throw new Error('Invalid response: missing cells array');
      }
      if (terrainStrings.length !== GRID_W * GRID_H) {
        throw new Error(
          `Expected ${GRID_W * GRID_H} cells, got ${terrainStrings.length}. ` +
          'Try a clearer image or a different crop.'
        );
      }

      // Normalize terrain IDs
      const terrainArr = terrainStrings.map((t) => {
        const tDef = TERRAIN_MAP[t];
        return tDef ? tDef.id : 'empty';
      });

      stopStepAnimation();
      const walls = json.walls || [];
      // Build cells[] for loadFullMap (legacy format — loadFullMap handles both)
      const cells = terrainArr.map((t) => ({
        terrain: t,
        passable: (TERRAIN_MAP[t] || TERRAIN_MAP.empty).passable,
        fogRevealed: false,
        elevation: 0,
      }));
      await loadFullMap({ cells, walls, doors: [], gridW: GRID_W, gridH: GRID_H });

      // Spawn detected objects
      const objects = json.objects || [];
      let objectCount = 0;
      for (const obj of objects) {
        const def = OBJECT_MAP[obj.type];
        if (!def) continue;
        // Skip objects on impassable terrain
        const idx = obj.y * GRID_W + obj.x;
        const tid = terrainArr[idx];
        if (tid && !(TERRAIN_MAP[tid] || TERRAIN_MAP.empty).passable) continue;
        await spawnToken({
          name: def.label,
          type: 'object',
          objectType: def.id,
          size: def.size || 1,
          hp: def.hp,
          maxHp: def.hp,
          colour: def.color,
          passable: def.passable || false,
          x: obj.x,
          y: obj.y,
        });
        objectCount++;
      }

      // Move player tokens to spawn point
      const sp = spawnPoint || { x: 1, y: 1 };
      const playerTokens = Object.values(tokens).filter((t) => t.type === 'player');
      for (const t of playerTokens) {
        await moveToken(t.id, sp.x, sp.y);
      }

      setStatus('success');
      const terrainCount = terrainArr.filter((t) => t !== 'empty').length;
      const parts = [`${terrainCount} terrain cells`];
      if (walls.length > 0) parts.push(`${walls.length} walls`);
      if (objectCount > 0) parts.push(`${objectCount} objects`);
      setMessage(`Map imported! ${parts.join(', ')}.`);

      // Auto-close after success
      setTimeout(() => {
        setStatus(null);
        setMessage('');
        setPreview(null);
        setOpen(false);
        if (fileRef.current) fileRef.current.value = '';
      }, 2000);

    } catch (err) {
      stopStepAnimation();
      setStatus('error');
      setMessage(err.message || 'Import failed');
    }
  };

  // ── Loading step label ────────────────────────────────────────────────────
  const loadingLabel = status === 'loading' ? STEPS[stepIdx] : null;

  return (
    <div style={styles.wrapper}>
      <button style={styles.toggle} onClick={() => setOpen((v) => !v)}>
        <span>🤖 AI Map Import</span>
        <span style={styles.chevron}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={styles.body}>
          <p style={styles.hint}>
            Upload a battle map image — Claude will analyze it and paint the terrain grid automatically.
          </p>

          {/* Hidden file input */}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={handleFile}
            style={{ display: 'none' }}
          />

          {/* Image preview or picker */}
          {preview ? (
            <div style={styles.previewWrapper}>
              <img src={preview} alt="Map preview" style={styles.preview} />
              <button style={styles.removeBtn} onClick={clearFile} title="Remove image">✕</button>
            </div>
          ) : (
            <button style={styles.uploadBtn} onClick={() => fileRef.current?.click()}>
              📁 Choose Map Image
            </button>
          )}

          {/* Status message */}
          {(message || loadingLabel) && (
            <div
              style={{
                ...styles.message,
                color: status === 'error'   ? '#e07070'
                      : status === 'success' ? '#5cb870'
                      :                        '#d4a847',
              }}
            >
              {status === 'loading' && <span style={styles.spinner}>⟳ </span>}
              {loadingLabel || message}
            </div>
          )}

          {/* Progress bar during loading */}
          {status === 'loading' && (
            <div style={styles.progressTrack}>
              <div
                style={{
                  ...styles.progressBar,
                  width: `${((stepIdx + 1) / STEPS.length) * 100}%`,
                  transition: 'width 2.4s ease',
                }}
              />
            </div>
          )}

          <button
            style={{
              ...styles.importBtn,
              opacity: !preview || status === 'loading' ? 0.4 : 1,
              cursor:  !preview || status === 'loading' ? 'not-allowed' : 'pointer',
            }}
            onClick={handleImport}
            disabled={!preview || status === 'loading'}
          >
            {status === 'loading' ? 'Importing…' : '✨ Import Map'}
          </button>

          <p style={styles.note}>
            Grid: {GRID_W}×{GRID_H} cells. The image will be scaled to fit.
          </p>
        </div>
      )}
    </div>
  );
}

const styles = {
  wrapper:      { borderTop: '1px solid #4a3d28', flexShrink: 0 },
  toggle:       { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'transparent', border: 'none', color: '#9a8b6e', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', cursor: 'pointer' },
  chevron:      { fontSize: 10, color: '#6a5d45' },
  body:         { padding: '0 14px 14px', display: 'flex', flexDirection: 'column', gap: 10 },
  hint:         { fontSize: 11, color: '#9a8b6e', lineHeight: 1.5, margin: 0 },

  uploadBtn:    { width: '100%', padding: '12px 0', background: '#151525', border: '2px dashed #4a3d28', borderRadius: 4, color: '#9a8b6e', fontSize: 13, cursor: 'pointer', textAlign: 'center', transition: 'border-color 0.15s, color 0.15s' },

  previewWrapper: { position: 'relative', width: '100%' },
  preview:        { width: '100%', borderRadius: 4, display: 'block', border: '1px solid #4a3d28', maxHeight: 160, objectFit: 'cover' },
  removeBtn:      { position: 'absolute', top: 6, right: 6, width: 24, height: 24, background: 'rgba(15,15,26,0.8)', border: '1px solid #4a3d28', borderRadius: '50%', color: '#9a8b6e', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, lineHeight: 1 },

  message:      { fontSize: 12, lineHeight: 1.4 },
  spinner:      { display: 'inline-block', animation: 'spin 1s linear infinite' },

  progressTrack: { height: 4, background: '#4a3d28', borderRadius: 2, overflow: 'hidden' },
  progressBar:   { height: '100%', background: 'linear-gradient(90deg, #7c3aed, #d4a847)', borderRadius: 2 },

  importBtn:    { width: '100%', padding: '10px 0', background: 'linear-gradient(135deg, #3a1060, #5a2090)', border: '1px solid #d4a847', borderRadius: 4, color: '#e2d4ff', fontSize: 13, fontWeight: 700, transition: 'opacity 0.15s' },

  note:         { fontSize: 10, color: '#6a5d45', margin: 0, textAlign: 'center' },
};
